import "./MaintenanceDashboard.css";
import { GridLayoutWidget, SelectedVehiclesFilterContext } from "./common";
import { useContext, useEffect, useState } from "react";
import { Button, Loading, Title } from "react-admin";
import {
  MaintenanceDashboardApiFactory,
  MaintenanceDashboardVehicleDto,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import { VehicleInfoWidget } from "./vehicle-widgets/VehicleInfoWidget";
import { VehicleWidgetProps } from "./vehicle-widgets/VehicleWidgetProps";
import { useHistory } from "react-router-dom";
import { VehicleGeometrisAlerts } from "./vehicle-widgets/VehicleGeometrisAlerts";
import { VehicleDiagnosticErrors } from "./vehicle-widgets/VehicleDiagnosticErrors";
import { VehicleSensorInfoWidget } from "./vehicle-widgets/VehicleSensorInfoWidget";
import { TpmsInfoWidget } from "./vehicle-widgets/TpmsInfoWidget";
import resourceNames from "../../../common/data/resourceNames";
import { ArrowBack } from "@material-ui/icons";
import { Breadcrumb } from "./Breadcrumb";
import { VehicleMaintenanceWidget } from "./vehicle-widgets/VehicleMaintenanceWidget";
import { UnresolvedVehicleDvirDefects } from "./vehicle-widgets/UnresolvedVehicleDvirDefects";
import { SelectedVehiclesSlide } from "./SelectedVehiclesSlide";
import "./VehicleMaintenanceDashboard.css";

const widgets: GridLayoutWidget<VehicleWidgetProps>[] = [
  {
    i: "vehicleInfo",
    Widget: VehicleInfoWidget,
    icon: "info",
    title: "resources.maintenance-dashboard.labels.vehicleInfo",
  },
  {
    i: "vehicleMaintenance",
    Widget: VehicleMaintenanceWidget,
    icon: "info",
    title: "Oil change due",
  },
  {
    i: "vehicleSensorInfo",
    Widget: VehicleSensorInfoWidget,
    icon: "info",
    title: "Vehicle sensor info",
  },
  {
    i: "tpms",
    Widget: TpmsInfoWidget,
    icon: "info",
    title: "TPMS info",
  },
  {
    i: "diag",
    Widget: VehicleDiagnosticErrors,
    icon: "error",
    title: "resources.maintenance-dashboard.labels.diagnosticErrors",
  },
  {
    i: "geometrisAlerts",
    Widget: VehicleGeometrisAlerts,
    icon: "warning",
    title: "resources.maintenance-dashboard.labels.vehicleAlerts",
  },
  {
    i: "vehicleDvirDefects",
    Widget: UnresolvedVehicleDvirDefects,
    icon: "warning",
    title: "Unresolved DVIR defects",
  },
];

const configurations: {
  [widget: string]: any;
} = {
  vehicleSensorInfo: [
    "ECU_MPG",
    "OBD_TRIP_MPG",
    "ECU_FUELLVL",
    "EXT_VOLTAGE",
    "ECU_OIL_TEMP",
    "J1939_ENGINE_OIL_LEVEL",
    "J1939_DIESEL_EXHAUST_FLUID_TEMPERATURE",
    "J1939_DIESEL_EXHAUST_FLUID_LEVEL",
    "J1939_DPF_SOOT_LOAD",
    "J1939_DPF_TIME_SINCE_LAST_ACTIVE_REGENERATION",
    "J1939_TRANSMISSION_OIL_TEMPERATURE",
  ],
};

interface Props {
  vehicleId?: number;
}

export const VehicleMaintenanceDashboard = ({ vehicleId }: Props) => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(true);
  const [vehicle, setVehicle] = useState<MaintenanceDashboardVehicleDto>();
  const axios = useAxiosInstance();
  const context = useContext(SelectedVehiclesFilterContext);
  const sidebarVisible = !!context?.selectedFilter;

  useEffect(() => {
    if (!vehicleId) {
      return;
    }

    if (!axios) {
      return;
    }

    setLoading(true);
    MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMaintenancedashboardIdVehicleGet(vehicleId)
      .then((response) => setVehicle(response.data))
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [vehicleId, axios]);

  return loading ? (
    <Loading />
  ) : vehicle ? (
    <div className="maintenance-dashboard">
      <Title title={<Breadcrumb vehicle={vehicle} />} />
      <section className="widgets-header">
        <>
          <Button
            onClick={() =>
              history.push(`/${resourceNames.ASSETS_MAINTENANCE_DASHBOARD}`)
            }
            label="Return to dashboard">
            <ArrowBack />
          </Button>
        </>
      </section>
      <div className="vehicle-maintenance-dashboard">
        {widgets.map((x) => {
          return (
            <x.Widget
              key={x.i}
              icon={x.icon}
              title={x.title}
              vehicle={vehicle}
              configuration={configurations[x.i]}
            />
          );
        })}
        {sidebarVisible ? <SelectedVehiclesSlide /> : null}
      </div>
    </div>
  ) : null;
};
